import * as React from "react";
// import { MicIcon, StopCircle } from "lucide-react";
import { Expand, Minimize2, StopCircle, Trash } from "lucide-react";
import { getCookie, getShadow360URL } from "@utils/utils";
import { Input } from "@components/ui/input";
import { ShadowGPTProps } from "./CommonTypes";
import { Button } from "@components/ui/button";
import { useLocation } from "react-router-dom";
import { marked } from "marked";
import PluginActionDialog from "@pages/homepage/aml-anomaly-detection/PluginActionDialog";
import { ArrowDown } from "lucide-react";
import DOMPurify from "dompurify";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@components/ui/tooltip";
import { useNavigate } from "react-router-dom";
import "@components/common/shadowgptStyles.css";

const ShadowGPT: React.FC<ShadowGPTProps> = ({
  className = "",
  searchString = "",
  externalSearch = false,
  stop = false,
  onFinish = () => {},
  data = {},
  globalChatHistory,
  setGlobalChatHistory = () => {},
  setIsExpanded,
  isExpanded,
}) => {
  // const [recording, setRecording] = React.useState(false);

  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  const [chatStatus, setChatStatus] = React.useState("");
  const [showConfirmClear, setShowConfirmClear] = React.useState(false);
  const [showPluginDialog, setShowPluginDialog] = React.useState(false);
  const [showDropButton, setShowDropButton] = React.useState(false);
  const [chatHistory, setChatHistory] = React.useState<
    { sender: "gpt" | "user"; message: string }[]
  >([
    {
      sender: "gpt",
      message: "Hi, I am ShadowGPT",
    },
  ]);
  const searchEventSourceRef = React.useRef<EventSource>();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const yourTextRef = React.useRef<HTMLSpanElement>(null);
  const chatResponseRef = React.useRef<HTMLDivElement>(null);
  const chatContainerRef = React.useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = React.useRef(true);

  const shadowGPTFlag = "true";
  let searchEventSource: any = null;

  const location = useLocation();
  const navigate = useNavigate();

  function stopSearch() {
    if (searchEventSourceRef.current) {
      searchEventSourceRef.current.onmessage = null;
      searchEventSourceRef.current.close();
    }
    const cacheKey = new Date().getTime();
    searchEventSource = new EventSource(
      process.env.API_URL +
        `/advancedSearch/?advancedSearchInput=/clear&cacheKey=${cacheKey}&shadowGPT=` +
        shadowGPTFlag,
      { withCredentials: true },
    );

    searchEventSourceRef.current = searchEventSource;

    searchEventSource.onmessage = function () {
      if (searchEventSourceRef.current) {
        searchEventSourceRef.current.onmessage = null;
        searchEventSourceRef.current.close();
      }
      if (chatResponseRef.current) {
        chatResponseRef.current.innerHTML =
          chatResponseRef.current.innerHTML +
          `</br><div class="text-primary font-bold">Stopped</div>`;
      }

      setChatStatus("stopped");
      if (externalSearch) {
        setGlobalChatHistory((prev) => {
          const updated = [...prev];
          const lastUserIndex = [...updated]
            .map((msg, index) => ({ ...msg, index }))
            .reverse()
            .find((msg) => msg.sender === "user")?.index;
          if (lastUserIndex !== undefined) {
            return updated.slice(0, lastUserIndex);
          } else {
            return updated.slice(0, 0); // or []
          }
        });
      } else {
        setChatHistory((prev) => {
          const updated = [...prev];
          const lastUserIndex = [...updated]
            .map((msg, index) => ({ ...msg, index }))
            .reverse()
            .find((msg) => msg.sender === "user")?.index;
          if (lastUserIndex !== undefined) {
            return updated.slice(0, lastUserIndex);
          } else {
            return updated.slice(0, 1);
          }
        });
      }
    };
  }
  React.useEffect(() => {
    if (searchString != "") {
      setGlobalChatHistory((prev) => [
        ...prev,
        { sender: "user", message: searchString },
      ]);
      startSearch(searchString);
    } else {
      if (searchEventSourceRef.current) {
        searchEventSourceRef.current.onmessage = null;
        searchEventSourceRef.current.close();
      }
      setChatStatus("");
    }
    return () => {
      if (searchEventSourceRef.current) {
        searchEventSourceRef.current.onmessage = null;
        searchEventSourceRef.current.close();
      }
    };
  }, [searchString]);

  React.useEffect(() => {
    if (chatStatus !== "thinking" && chatStatus !== "writing") {
      inputRef.current?.focus();
    }
  }, [chatStatus]);

  React.useEffect(() => {
    if (shouldAutoScrollRef.current) {
      scrollToBottom();
    }
  }, [chatHistory, globalChatHistory]);

  function searchGPT(e: any) {
    let text: string = "";
    if (inputRef.current) {
      text = inputRef.current?.value.trim();
    }
    if (e.key == "Enter") {
      if (text.toLowerCase() === "/clear") {
        setChatHistory((prev) =>
          prev.filter((msg) => msg.message === "Hi, I am ShadowGPT"),
        );
        setChatStatus("");
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        startSearch(text);
        return;
      }

      if (chatStatus == "free" || chatStatus == "" || chatStatus == "stopped") {
        if (text.trim().toLowerCase() !== "/clear") {
          setChatHistory((prev) => [
            ...prev,
            { sender: "user", message: text },
          ]);
        }
        startSearch(text);
        if (inputRef.current) {
          inputRef.current.value = "";
        }
      }
    } else {
      if (searchEventSourceRef.current) {
        searchEventSourceRef.current.onmessage = null;
        searchEventSourceRef.current.close();
      }
      setChatStatus("");
    }
  }

  function createCaseTab() {
    sessionStorage.setItem(
      "anomIds",
      `[{"id": ${data.globalID}, "time": ${data.epoch_time}}]`,
    );
    sessionStorage.setItem("anomtype", data.anomaly_type ?? "");
    sessionStorage.setItem("timeFrame", "day");

    data["question"] = "create case";
    window.open(
      `/case-management/?create=1&incidentParams=${encodeURIComponent(JSON.stringify(data))}`,
      "_blank",
    );
  }

  function startSearch(text: string) {
    if (yourTextRef.current) {
      yourTextRef.current.innerText = text;
    }
    setChatStatus("thinking");
    if (chatResponseRef.current) {
      chatResponseRef.current.innerHTML = "";
    }

    // if (hiddenResponseRef.current) {
    //   hiddenResponseRef.current.innerHTML = "";
    // }

    const options: any = {
      headers: {},
    };

    const csrfToken = getCookie("csrftoken");
    if (csrfToken) {
      options.headers["X-CSRFToken"] = csrfToken;
    }

    data["question"] = text;

    const cacheKey = new Date().getTime();
    searchEventSource = new EventSource(
      process.env.API_URL +
        `/advancedSearch/?advancedSearchInput=` +
        encodeURIComponent(JSON.stringify(data)) +
        `&cacheKey=${cacheKey}&shadowGPT=` +
        shadowGPTFlag,
      { withCredentials: true },
    );
    searchEventSourceRef.current = searchEventSource;

    let responseRawMessage = "";
    searchEventSource.onmessage = function (e: any) {
      let response = e.data;
      if (response == "finished") {
        if (searchEventSourceRef.current) {
          searchEventSourceRef.current.onmessage = null;
          searchEventSourceRef.current.close();
        }
        if (onFinish) {
          onFinish();
        }
      } else {
        response = JSON.parse(response);
        if (response.status == "success") {
          if ("shadowbot" in response) {
            const bot = response.shadowbot;

            if (bot == "__chat_busy__") {
              setChatStatus("thinking");
            } else if (bot == "__chat_ended__") {
              setChatStatus("free");
            } else if (bot == "__chat_stopped__") {
              setChatStatus("stopped");
            } else if (bot == "__create_case__") {
              createCaseTab();
            } else if (bot == "__automation__") {
              const url: string = `/automate-case/?anomIds=[{"id":"${data.globalID ?? data.globalID}","time":"${data.epoch_time ?? data.epoch_time}"}]&timeFrame=day&anomtype=${data.anomaly_type ?? data.anomaly_type}`;
              window.open(url);
            } else if (bot == "__plugin_action__") {
              setShowPluginDialog(true);
            } else if (
              bot.startsWith("__notification_start__") &&
              bot.endsWith("__notification_end__")
            ) {
              const notificationContent = bot
                .replace("__notification_start__", "")
                .replace("__notification_end__", "");
              setChatStatus(notificationContent);
            } else if (bot == "__s360_search__") {
              const shadowProps: any = {};

              shadowProps["anomalyId"] = data.globalID;
              shadowProps["startTime"] = data.epoch_time;
              shadowProps["endTime"] = data.epoch_time;
              shadowProps["logTypes"] = [];
              shadowProps["searchValue"] = data.anomalous_ip;
              shadowProps["searchType"] = "ip";
              shadowProps["sensor"] = "";

              const s360Url: string = getShadow360URL(shadowProps);
              window.open(s360Url);
            } else {
              setChatStatus("writing");
              // if (bot.includes("\n")) {
              //   bot = bot.replace(/\n/g, "\n<br>");
              // }
              if (text.trim().toLowerCase() !== "/clear") {
                if (externalSearch) {
                  setGlobalChatHistory((prev) => {
                    const last = prev[prev.length - 1];
                    if (last && last.sender === "gpt") {
                      const updated = [...prev];
                      updated[updated.length - 1] = {
                        ...last,
                        message: last.message + bot,
                      };
                      return updated;
                    } else {
                      return [...prev, { sender: "gpt", message: bot }];
                    }
                  });
                } else {
                  setChatHistory((prev) => {
                    const last = prev[prev.length - 1];
                    if (last && last.sender === "gpt") {
                      const updated = [...prev];
                      updated[updated.length - 1] = {
                        ...last,
                        message: last.message + bot,
                      };
                      return updated;
                    } else {
                      return [...prev, { sender: "gpt", message: bot }];
                    }
                  });
                }
              }

              responseRawMessage += bot;

              if (chatResponseRef.current) {
                chatResponseRef.current.innerHTML = marked.parse(
                  responseRawMessage,
                ) as string;
              }
            }
          }
        } else {
          showError();
        }
      }
    };

    searchEventSource.onerror = function () {
      showError();
    };
  }

  // function modifyAdvanceResult() {
  //   if (chatResponseRef.current && hiddenResponseRef.current) {
  //     const searchResults = hiddenResponseRef.current.innerHTML.trim();
  //     let modifiedSearchResults: string = searchResults;

  //     // check if word is wrapped in * WORD * and convert into a list
  //     modifiedSearchResults = modifiedSearchResults.replace(
  //       /\*([^*\n]+)\n\s/g,
  //       "<br><b>$1</b>\n",
  //     );

  //     chatResponseRef.current.innerHTML = modifiedSearchResults;
  //   }
  // }

  function showError() {
    setChatStatus("free");
    if (chatResponseRef.current) {
      chatResponseRef.current.innerHTML = `<div class="text-primary">An error occured! Please try again later</div>`;
    }
  }

  React.useEffect(() => {
    if (stop) {
      stopSearch();
    }
  }, [stop]);

  const shadowGPTStatus = sessionStorage.getItem("shadowGPTStatus");
  if (!shadowGPTStatus || shadowGPTStatus != "on") {
    return <></>;
  }

  const clearChatHistory = () => {
    setChatHistory([{ sender: "gpt", message: "Hi, I am ShadowGPT" }]);
    setChatStatus("");
    startSearch("/clear");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };
  React.useEffect(() => {
    return () => {
      startSearch("/clear");
    };
  }, [location.pathname]);

  React.useEffect(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, [isExpanded]);

  const handleChatContentClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const entityLink = target.closest<HTMLAnchorElement>(
      "a[data-entity-type][data-entity-value]",
    );

    if (!entityLink) {
      return;
    }

    event.preventDefault();

    const entityType = entityLink.dataset.entityType;
    const entityValue = entityLink.dataset.entityValue;

    if (!entityValue) {
      return;
    }

    switch (entityType) {
      case "email":
        navigate({ hash: "" }, { replace: true });
        navigate({
          hash: `#userinvestigator=${entityValue}`,
        });
        break;

      case "ip":
        navigate({ hash: "" }, { replace: true });
        navigate({
          hash: `#entity=${encodeURIComponent(entityValue)}`,
        });
        break;

      default:
        break;
    }
  };

  function enhanceMarkdownHtml(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const emailRegex = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;

    const ipRegex =
      /\b(?:(?:25[0-5]|2[0-4]\d|1?\d{1,2})\.){3}(?:25[0-5]|2[0-4]\d|1?\d{1,2})\b/g;

    /*
     * marked may already convert email addresses into mailto links.
     * Add the required attributes to those existing links.
     */
    doc
      .querySelectorAll<HTMLAnchorElement>('a[href^="mailto:"]')
      .forEach((anchor) => {
        const href = anchor.getAttribute("href");

        if (!href) {
          return;
        }

        const email = decodeURIComponent(
          href.replace(/^mailto:/i, "").split("?")[0],
        );

        anchor.dataset.entityType = "email";
        anchor.dataset.entityValue = email;
        anchor.classList.add("chat-entity-link");
      });

    /*
     * Process plain text containing emails or IP addresses.
     */
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);

    const textNodes: Text[] = [];

    while (walker.nextNode()) {
      const node = walker.currentNode as Text;
      const parent = node.parentElement;

      if (parent && !parent.closest("a, code, pre, script, style")) {
        textNodes.push(node);
      }
    }

    for (const textNode of textNodes) {
      const text = textNode.textContent ?? "";

      const matches = [
        ...Array.from(text.matchAll(emailRegex)).map((match) => ({
          value: match[0],
          type: "email",
          index: match.index ?? 0,
        })),
        ...Array.from(text.matchAll(ipRegex)).map((match) => ({
          value: match[0],
          type: "ip",
          index: match.index ?? 0,
        })),
      ].sort((a, b) => a.index - b.index);

      if (matches.length === 0) {
        continue;
      }

      const fragment = doc.createDocumentFragment();
      let currentIndex = 0;

      for (const match of matches) {
        if (match.index < currentIndex) {
          continue;
        }

        fragment.append(
          doc.createTextNode(text.slice(currentIndex, match.index)),
        );

        const anchor = doc.createElement("a");

        anchor.href = "#";
        anchor.textContent = match.value;
        anchor.dataset.entityType = match.type;
        anchor.dataset.entityValue = match.value;
        anchor.classList.add("chat-entity-link");

        fragment.append(anchor);

        currentIndex = match.index + match.value.length;
      }

      fragment.append(doc.createTextNode(text.slice(currentIndex)));

      textNode.replaceWith(fragment);
    }

    return doc.body.innerHTML;
  }

  function renderMarkdown(message: string): string {
    const parsedHtml = marked.parse(message) as string;
    const enhancedHtml = enhanceMarkdownHtml(parsedHtml);
    const sanitizedHtml = DOMPurify.sanitize(enhancedHtml, {
      ADD_ATTR: ["target", "rel", "data-entity-type", "data-entity-value"],
    });
    return sanitizedHtml;
  }

  function scrollToBottom() {
    if (!chatContainerRef.current) return;
    chatContainerRef.current?.scrollTo({
      top: chatContainerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }

  function handleScroll() {
    const container = chatContainerRef.current;
    if (!container) return;

    const threshold = 100;

    const isAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      threshold;

    shouldAutoScrollRef.current = isAtBottom;

    setShowDropButton(!isAtBottom);
  }

  function handleExpandCollapse() {
    setIsExpanded && setIsExpanded((prev) => !prev);
  }

  return (
    <>
      {showPluginDialog && (
        <PluginActionDialog
          show={showPluginDialog}
          onClose={() => setShowPluginDialog(false)}
          anmlId={data.globalID || 0}
          anomalyTime={data.epoch_time || 0}
          anomalyType={data.anomaly_type || ""}
        />
      )}
      {/* <button onClick={() => setShowPluginDialog(true)}>test</button> */}
      <div
        className={
          "relative rounded-md overflow-y-auto border border-[#ffffff30] p-4 " +
          className
        }
      >
        {!externalSearch && (
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <h4 className="text-xl shadowGPT-heading">ShadowGPT</h4>
            </div>
            <div className="flex items-center">
              {chatHistory.length > 1 && (
                <div className="flex items-center space-x-2">
                  {!showConfirmClear ? (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="flex items-center gap-1 clear-chat-btn cursor-pointer"
                            onClick={() => setShowConfirmClear(true)}
                          >
                            <Trash size={14} />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent collisionPadding={80} side={"left"}>
                          <div className={className}>Clear Chat</div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <>
                      <Button
                        className="clear-btn"
                        onClick={() => {
                          clearChatHistory();
                          setShowConfirmClear(false);
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        className="cancel-btn"
                        onClick={() => setShowConfirmClear(false)}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              )}
              <div
                className="flex justify-end items-center clear-chat-btn cursor-pointer"
                onClick={handleExpandCollapse}
              >
                {isExpanded ? (
                  <Minimize2
                    className="expand-icon cursor-pointer transition-all"
                    size={14}
                  />
                ) : (
                  <Expand
                    className="expand-icon cursor-pointer transition-all"
                    size={14}
                  />
                )}
              </div>
            </div>
          </div>
        )}
        <div
          ref={chatContainerRef}
          onScroll={handleScroll}
          className={`text-xs whitespace-normal gpt-container mt-7 overflow-y-auto ${isExpanded ? "h-[80%]" : "h-[72%]"} space-y-2`}
        >
          {(externalSearch
            ? (globalChatHistory ?? [])
            : (chatHistory ?? [])
          ).map((chat, index) => {
            if (chat.message.trim() === "__list_tools__") return null;
            return (
              <div className="flex justify-end items-start gap-3" key={index}>
                {chat.sender !== "user" && (
                  <img
                    src={process.env.STATIC_PATH + "/img/shadowGPT/Circle.gif"}
                    className="w-[35px] h-[35px] rounded-full pointer-events-none mt-8 shadowgpt-image"
                    alt=""
                  />
                )}
                <div
                  ref={messagesEndRef}
                  onClick={handleChatContentClick}
                  className={`inline-block w-auto p-2 rounded-md shadow-md my-3 shadow-gpt-chat-container  ${
                    chat.sender === "user"
                      ? "bg-primary-background text-foreground bg-opacity-5 self-end ml-auto text-left backdrop-blur-sm shadowGPT-user-response mr-2"
                      : "text-white self-start mr-auto text-left shadowGPT-response-chat ml-2"
                  }`}
                  style={{ maxWidth: "80%" }}
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(chat.message),
                  }}
                ></div>
              </div>
            );
          })}
          {chatStatus &&
            chatStatus !== "writing" &&
            chatStatus !== "free" &&
            chatStatus !== "stopped" && (
              <div className="flex justify-end items-start gap-3">
                <img
                  src={process.env.STATIC_PATH + "/img/shadowGPT/Circle.gif"}
                  className="w-[35px] h-[35px] rounded-full pointer-events-none shadowgpt-image mt-9"
                  alt=""
                />
                <div
                  className={`flex items-center gap-2 inline-block p-2 rounded-md shadow-md break-all word-wrap my-3 text-white self-start mr-auto text-left chat-status-container w-[50%]`}
                >
                  <div className="flex items-center gap-1">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-[#9D3135] [animation-delay:-0.4s]" />
                    <span className="h-2 w-2 animate-pulse rounded-full bg-[#9D3135] [animation-delay:-0.2s]" />
                    <span className="h-2 w-2 animate-pulse rounded-full bg-[#9D3135]" />
                  </div>
                  <div className="overflow-hidden">
                    <p
                      key={chatStatus}
                      className="animate-[statusSlideUp_0.35s_ease-out] whitespace-nowrap"
                    >
                      {chatStatus === "thinking" ? "Thinking..." : chatStatus}
                    </p>
                  </div>
                </div>
              </div>
            )}
        </div>
        {showDropButton && (
          <div
            className="absolute bottom-[20%] left-[42%] p-2 rounded-full bg-primary text-primary-foreground cursor-pointer"
            onClick={scrollToBottom}
          >
            <ArrowDown className="scroll-down-icon" size={18} />
          </div>
        )}
        {/* <div ref={hiddenResponseRef} className="hidden"></div> */}

        {!externalSearch ? (
          <div className="absolute bottom-4 left-0 w-full">
            <div className="relative p-4">
              <Input
                className="text-sm shadowgpt-ask-input
                  h-auto
                  rounded-full
                  border-transparent
                  px-[18px]
                  py-[16px]
                  text-sm
                  focus-visible:outline-none
                  focus-visible:ring-0
                  focus-visible:ring-offset-0"
                onKeyUp={(e: any) => searchGPT(e)}
                ref={inputRef}
                disabled={
                  chatStatus === "thinking" ||
                  chatStatus === "writing" ||
                  chatStatus === "checking for tools..."
                }
                placeholder="Ask ShadowGPT..."
              />
              {(chatStatus === "thinking" || chatStatus === "writing") && (
                <StopCircle
                  size={35}
                  onClick={stopSearch}
                  className="absolute top-[2.9rem] right-7 -translate-y-2/4 cursor-pointer hover:text-primary transition-all text-[#ff6565ab]"
                />
              )}
              {/* case number 1172 */}
              {/* <MicIcon
                size={26}
                onClick={() => setRecording(!recording)}
                className={
                  "absolute top-2/4 right-4 -translate-y-2/4 cursor-pointer hover:text-primary transition-all border rounded-full p-1 " +
                  (recording
                    ? "border-primary text-primary animate-pulse"
                    : "") +
                  (chatStatus == "free" || chatStatus == "" ? "" : "hidden")
                }
              /> */}
            </div>
          </div>
        ) : (
          ""
        )}
      </div>
    </>
  );
};

export default ShadowGPT;
